/**
 * Dialogs over a screen. `dialog` is the box; `confirm` is the yes/no built on it, used only
 * where a loss is permanent (08 §5.3: retire a veteran, withdraw an operation).
 */

import './chrome.css';
import type { Ctx, Modal } from '../context';
import { box, button, el } from '../dom';

export interface Dialog {
  modal: Modal;
  body: HTMLElement;
  actions: HTMLElement;
}

/** An empty titled dialog. `onCancel` runs on Esc; it should close the modal. */
export function dialog(title: string, onCancel: () => void, extraClass = ''): Dialog {
  const root = el('div', extraClass === '' ? 'menu-dialog' : `menu-dialog ${extraClass}`);
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', title);
  const body = el('div', 'menu-dialog-body menu-col menu-scroll');
  const actions = el('div', 'menu-dialog-actions');
  root.append(el('div', 'menu-dialog-title', title), body, actions);
  return { modal: { el: root, cancel: onCancel }, body, actions };
}

export interface ConfirmOptions {
  title: string;
  /** What will happen, in plain words; one paragraph per entry. */
  lines: string[];
  confirmLabel: string;
  cancelLabel?: string;
  /** Destructive: the confirm button is red and focus starts on cancel. */
  danger?: boolean;
  onConfirm(): void;
}

export function confirm(ctx: Ctx, options: ConfirmOptions): void {
  const close = (): void => ctx.closeModal();
  const d = dialog(options.title, close);
  for (const line of options.lines) d.body.append(box('menu-prose', line));
  const yes = button(
    options.confirmLabel,
    () => {
      close();
      options.onConfirm();
    },
    options.danger === true ? 'menu-btn-danger' : '',
  );
  const no = button(options.cancelLabel ?? 'CANCEL', close);
  d.actions.append(no, yes);
  const focus = options.danger === true ? no : yes;
  ctx.openModal({ ...d.modal, initialFocus: () => focus });
}

/** A one-button notice: the log line from a support node, an error from the API. */
export function notice(ctx: Ctx, title: string, lines: string[], onClose: () => void = () => undefined): void {
  const close = (): void => {
    ctx.closeModal();
    onClose();
  };
  const d = dialog(title, close);
  for (const line of lines) d.body.append(box('menu-prose', line));
  const ok = button('ACKNOWLEDGED', close);
  d.actions.append(ok);
  ctx.openModal({ ...d.modal, initialFocus: () => ok });
}
